import { ReactNode, useRef, useState } from "react"
import { Card, Collapse, List, Modal, Popover, Table, TableColumnsType, Typography } from "antd"
import { InfoCircleOutlined, HolderOutlined, AppstoreAddOutlined } from "@ant-design/icons";
import { t } from "i18next";

import { CSS } from "@dnd-kit/utilities";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { Event, GamePreferences, GameProposition } from "../../api.types";
import { Tables } from "./Tables";
import { toGameEntryView, GameEntryView } from "../../model/gameEntryView";
import { EventHeader } from "./EventHeader";
import { useLocalStorage } from "../../hooks/useLocalStorage";

export function EventPreferencesResult(props: { event: Event, onPreferenceChange: (fromId: string, toId: string) => any }): (JSX.Element | null) {
    const [swap, setSwap] = useState<[string, string] | undefined>()
    const [selected, setSelected] = useState<number | undefined>()
    const tablesRef = useRef()
    const [tablesExpanded, setTablesExpanded] = useLocalStorage<boolean>("user.settings.tablesExpanded", true)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
                distance: 1,
            },
        }),
    );

    const preferences = props.event.preferences || { dropOfGameIds: [], userPreferences: [] }
    const games = toGameEntryView(props.event)

    const columns: TableColumnsType<GameEntryView> = [
        {
            dataIndex: "name",
            title: "Gra",
            render: (value, record: GameEntryView, index): ReactNode => {
                return <GameInfoCell game={record} myProposition={(props.event.myPropositions || []).findIndex((id) => (""+id.id) === record.id) !== -1}/>
            },
        },
        {
            title: "Punkty",
            dataIndex: "pointSum",
            render: PointsCell
        }
    ]

    for (let idx = 0; idx < preferences.userPreferences.length; idx++) {
        columns.push({
            onCell: (gameEntry: GameEntryView, index?: number): CellProps | any => {
                if (index === undefined) {
                    return {}
                }
                const myPreferences = preferences.userPreferences[idx].myPreferences
                const border = myPreferences ? "1px #ddd solid" : undefined;
                const style: React.CSSProperties = {
                    textAlign: "center",
                    borderLeft: border,
                    borderRight: border
                }

                let props: CellProps = {
                    style: style,
                    rowIndex: index,
                    colIndex: idx,
                    gameId: gameEntry.id,
                    points: gameEntry.points[idx],
                }
                if (gameEntry.propositions[idx]) {
                    props.style.backgroundColor = "powderblue"
                }
                if (myPreferences) {
                    if (selected === index) {
                        props.style.backgroundColor = "cornflowerblue"
                    } else if (selected !== undefined && games[selected].points[idx] > (gameEntry.points[idx] || 0)) {
                        props.style.backgroundColor = "lightgreen"
                    }
                    props = Object.assign(props, {
                        onClick:
                            () => {
                                if (selected !== undefined && games[selected].points[idx] > (gameEntry.points[idx] || 0)) {
                                    setSwap([games[selected].id, games[index].id])
                                    setSelected(undefined)
                                } else if (gameEntry.dropOff && gameEntry.points[idx]) {
                                    setSelected(selected === index ? undefined : index)
                                }
                            }
                    })
                }
                return props
            },
            render: (value, record, index): any => {
                return (
                    <GamePreferenceCell colIdx={idx} rowIdx={index} preferences={preferences} game={record} />
                )
            }
        })
    }
    columns.push({
        width: 1,
        render: (value, record, index): ReactNode => {
            return <AppstoreAddOutlined onClick={() => {
                (tablesRef?.current as any)?.createTable(+record.id, record.name, record.notes.join(",\n"))
            }} />
        }
    })
    return (
        <>
            <Card style={{ width: "100%" }}>
                <EventHeader event={props.event} />

                <Collapse
                    style={{marginBottom: 20}}
                    activeKey={tablesExpanded ? ["tables"] : []}
                    items={[
                        {
                            key: "tables",
                            label: <Typography.Title level={4} style={{textAlign: "left", margin: 0}}>{t("Game Tables")}</Typography.Title>,
                            children: <Tables eventId={props.event.id} ref={tablesRef} />
                        }
                    ]}
                    onChange={() => {
                        setTablesExpanded(!tablesExpanded)
                    }}
                />

                <DndContext
                    sensors={sensors}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={(evt) => {
                        const fromId = evt.active.data.current ? evt.active.data.current.gameId : null
                        const fromPoints = evt.active.data.current ? evt.active.data.current.points : null
                        const toId = evt.over?.data.current ? evt.over.data.current.gameId : null
                        const toPoints = evt.over?.data.current ? evt.over.data.current.points : null
                        if (!toPoints || fromPoints > toPoints) {
                            setSwap([fromId, toId])
                        }
                    }}
                >
                    <Table
                        pagination={false}
                        showHeader={true}
                        dataSource={games}
                        columns={columns}
                        rowKey={"id"}
                        components={{
                            body: {
                                cell: MyCell
                            }
                        }}
                        style={{overflow: "scroll"}}
                    />
                </DndContext>
                <Modal
                    open={swap !== undefined}
                    onCancel={() => setSwap(undefined)}
                    onOk={() => {
                        if (swap) { props.onPreferenceChange(swap[0], swap[1]) }
                        setSwap(undefined)
                    }}
                    destroyOnClose={true}
                    title="Czy jesteś pewny?"
                    okText="Tak"
                    cancelText="Anuluj"
                    width={400}
                >
                    <div>Przenosisz swój głos z <GameBox game={props.event.propositions?.find((val) => swap?.[0] === val.gameId)} /> na <GameBox game={props.event.propositions?.find((val) => swap?.[1] === val.gameId)} /></div>
                </Modal>
            </Card>
        </>
    )
}

function GameBox(props: { game?: GameProposition }): JSX.Element | null {
    const { game } = props
    if (!game) { return null }
    return (
        <div style={{ display: "flex", alignItems: "center", margin: 5 }}>
            {game.thumbnail ? <div style={{ width: 32, height: 32, flexShrink: 0, justifyContent: "center", display: "flex" }}><img src={game.thumbnail} style={{ maxWidth: 32, maxHeight: 32, margin: "auto" }} alt="" /></div> : null}
            <div style={{ display: "inline-flex" }}>&nbsp;<a href={"https://boardgamegeek.com/boardgame/" + game.gameId}>{game.name}</a></div>
        </div>
    )
}

interface GameInfo {
    id: string
    name: string
    thumbnail: string
    notes?: string[]
    dropOff?: boolean
}

export function GameInfoCell(props: { game: GameInfo, myProposition: boolean }): JSX.Element {
    const game = props.game
    
    return (
        <div style={{ display: "flex", alignItems: "center", opacity: (game.dropOff ? "70%" : undefined), filter: (game.dropOff ? "grayscale(0.5)" : undefined) }}>
            {game.thumbnail ? <div style={{ width: 32, height: 32, flexShrink: 0, justifyContent: "center", display: "flex" }}><img src={game.thumbnail} style={{ maxWidth: 32, maxHeight: 32, margin: "auto" }} alt="" /></div> : null}
            <div style={{ display: "inline-flex" }}>&nbsp;<a href={"https://boardgamegeek.com/boardgame/" + game.id}>{game.name}</a></div>

            {(game.notes && game.notes.length > 0) || props.myProposition ?
                <Popover content={<GameInfoPopover notes={game.notes || []} myProposition={!!props.myProposition}/>} trigger="click">
                    <InfoCircleOutlined style={{ marginLeft: 5 }} />
                </Popover>
                : null}
        </div>
    )
}

function GameInfoPopover(props: {notes: string[], myProposition: boolean}): JSX.Element {
    const {notes, myProposition} = props
    const list = <List
        header={t("Notes")}
        size="small"
        dataSource={notes}
        renderItem={(item: string) => <List.Item>{item}</List.Item>}
    />
    return <>
    {myProposition ? <Typography>{t("Your proposition")}</Typography> : undefined}
    {notes.length > 0 ? list : undefined}
    </>
}

export function GamePreferenceCell(props: { game: GameEntryView, colIdx: number, rowIdx: number, preferences: GamePreferences }): JSX.Element | null {
    const record = props.game
    const { colIdx, preferences } = props
    const dropOff = record.dropOff
    const myPreferences = preferences.userPreferences[colIdx].myPreferences
    const draggable = dropOff && myPreferences

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: "pref_" + record.id + "_" + colIdx,
        disabled: !draggable,
        data: {
            gameId: record.id,
            points: record.points[colIdx]
        }
    });

    if (!record.points[colIdx]) {
        return null
    }

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.7 : 1,
        display: "flex",
        justifyContent: "center",
        position: "relative",
        zIndex: isDragging ? 2 : 1,
        cursor: draggable ? "ns-resize" : undefined,
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {record.points[colIdx]}
            {!(dropOff && preferences.userPreferences[colIdx].myPreferences && record.points[colIdx]) ? undefined :
                <span style={{ display: "inline-flex", alignContent: "center", flexWrap: "wrap" }}><HolderOutlined /></span>
            }
        </div>
    )
}

interface CellProps {
    style: React.CSSProperties
    rowIndex: number
    colIndex: number
    gameId: string
    points: number
    [id: string]: any
}

function MyCell(props: CellProps) {
    const { style, rowIndex, colIndex, gameId, points, ...propsRest } = props
    const { setNodeRef, isOver, active } = useDroppable({
        id: "drop_" + gameId + "_" + colIndex,
        data: { gameId, points }
    })

    const activePoints = active?.data?.current ? active.data.current.points as number : -1
    const droppable = isOver && (!points || activePoints > points)
    return <td
        ref={setNodeRef}
        style={Object.assign({}, style, droppable ? { backgroundColor: "lightgreen" } : {})}
        {...propsRest}
    />
}

function PointsCell(value: any, record: GameEntryView, index: number): ReactNode {
    let pointsText;
    if (!record.pointExtra) {
        pointsText = record.pointSum
    } else {
        pointsText = (record.pointSum + record.pointExtra) + " (" + record.pointSum + "+" + record.pointExtra + ")"
    }

    return <>
        {pointsText}
        {record.propositionCount > 1 ? <div style={{fontSize: "8px", color: "gray"}}>{t('entriesN', {count: record.propositionCount})}, +{t('pointsN', {count: record.pointExtra})}</div> : null}
    </>
}
